import axios, { AxiosError, AxiosInstance } from 'axios';

// 生产环境使用相对路径（nginx会代理/api请求），开发环境使用localhost:8000
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Required for HTTPOnly cookie auth
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add any request preprocessing here
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Unauthorized - session expired or not authenticated
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    if (error.response?.status === 403) {
      // Forbidden - no admin permission
      window.dispatchEvent(new CustomEvent('auth:forbidden'));
    }
    return Promise.reject(error);
  }
);

export interface ApiError {
  detail: string;
  status: number;
}

export function isApiError(error: unknown): error is AxiosError<ApiError> {
  return axios.isAxiosError(error);
}

export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    const axiosError = error as AxiosError<ApiError>;
    
    // 网络错误：无法连接到服务器
    if (!axiosError.response) {
      if (axiosError.code === 'ECONNREFUSED' || axiosError.message.includes('Network Error')) {
        return '无法连接到服务器，请确保后端服务已启动（端口8000）';
      }
      if (axiosError.code === 'ETIMEDOUT' || axiosError.message.includes('timeout')) {
        return '请求超时，请检查网络连接或服务器状态';
      }
      return `网络错误: ${axiosError.message || '无法连接到服务器'}`;
    }
    
    // 业务错误：从响应中获取详情
    if (axiosError.response?.data) {
      const errorData = axiosError.response.data as ApiError;
      if (errorData.detail) {
        return errorData.detail;
      }
    }
    return axiosError.message || '请求失败';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return '未知错误';
}
